// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Rediger › Geometri's pure logic (frontend-u0o.2, read-only slice S1):
 * the element tree by storey (Vægge, Åbninger, Rum, Dæk & tag) with each
 * row's provenance badges, the inspector's fields, the client-side checks
 * of the Advarsler panel, and picking a wall or opening on the plan. Works
 * on the rows `GET /storeys`, `/walls`, `/openings`, `/spaces` and `/slabs`
 * return. Pure: no React, no DOM.
 */

import type { SolidOpening, SolidSlab, SolidSpace, SolidWall, Stair, Storey, Zone } from '../api/types';
import { openingLabel, type XY } from './planWalls';
import { formatLength } from './ruler';
import { formatAreaDa } from './spaces';

export interface GeometriModel {
  storeys: readonly Storey[] | null;
  walls: readonly SolidWall[] | null;
  openings: readonly SolidOpening[] | null;
  spaces: readonly SolidSpace[] | null;
  slabs: readonly SolidSlab[] | null;
  /** `GET /zones` (S6): named groups of spaces; absent / null before it is read or on an older server. */
  zones?: readonly Zone[] | null;
  /** `GET /stairs` (S8): stairs by run; absent / null before it is read or on an older server. */
  stairs?: readonly Stair[] | null;
}

export type GeoKind = 'wall' | 'opening' | 'space' | 'slab' | 'stair';

/** A row's origin; a slab is derived from the spaces and storeys, never stored on its own. */
export type GeoSource = 'detected' | 'manual' | 'imported' | 'derived';

/** Below this confidence a row counts as uncertain ("kun usikre"). */
export const UNCERTAIN_CONFIDENCE = 0.6;

export interface TreeItem {
  guid: string;
  kind: GeoKind;
  label: string;
  source: GeoSource;
  locked: boolean;
  /** Edited by hand: the row's metadata carries `edited_from` (written by the edit API, ruxd-5uv.1). */
  edited: boolean;
  /** Low confidence, or a wall whose thickness was only assumed. */
  uncertain: boolean;
  /** Advarsler that name it. */
  warnings: number;
}

export interface TreeGroup {
  kind: GeoKind;
  label: string;
  items: TreeItem[];
}

export interface TreeStorey {
  /** `Storey.id`; null for the elements no stored storey holds. */
  id: number | null;
  name: string;
  groups: TreeGroup[];
  /** Elements on it (after the filter). */
  count: number;
}

const GROUP_LABEL: Record<GeoKind, string> = { wall: 'Vægge', opening: 'Åbninger', space: 'Rum', slab: 'Dæk & tag', stair: 'Trapper' };
const GROUP_ORDER: readonly GeoKind[] = ['wall', 'opening', 'space', 'slab', 'stair'];
const NO_STOREY = 'Uden etage';

// ------------------------------------------------------------- labels --

export function wallLabel(w: Pick<SolidWall, 'id'>): string {
  return `Væg ${w.id}`;
}

export function spaceLabel(s: Pick<SolidSpace, 'id' | 'name'>): string {
  return s.name.trim() !== '' ? s.name : `Rum ${s.id}`;
}

const SLAB_KIND: Record<SolidSlab['kind'], string> = { floor: 'Dæk', ceiling: 'Loft', roof: 'Tag' };

/** "Dæk", "Dæk 2", "Tag", "Loft"; a slab drawn by hand (part 1000+) or not built yet: "Dæk (manuel)" is left to the caller. */
export function slabLabel(s: Pick<SolidSlab, 'kind' | 'part'> & { id?: number }): string {
  const base = SLAB_KIND[s.kind] ?? 'Dæk';
  if (s.part === null || s.part >= 1000) return s.id !== undefined ? `${base} ${s.id}` : base;
  return s.part > 0 ? `${base} ${s.part + 1}` : base;
}

/** Written by a model edit: the server's `edited` flag (ruxd-5uv), else `metadata.edited` / `edited_from`. */
function isEdited(metadata: unknown, flag?: boolean): boolean {
  if (flag !== undefined) return flag;
  return typeof metadata === 'object' && metadata !== null && ('edited_from' in metadata || (metadata as { edited?: unknown }).edited === true);
}

function isUncertain(confidence: number | null, assumed = false): boolean {
  return assumed || (confidence !== null && confidence < UNCERTAIN_CONFIDENCE);
}

/** One tree row per element, without its storey. */
function itemsOf(model: GeometriModel): { storey: number | null; item: Omit<TreeItem, 'warnings'> }[] {
  const out: { storey: number | null; item: Omit<TreeItem, 'warnings'> }[] = [];
  const hostStorey = new Map((model.walls ?? []).map((w) => [w.guid, w.storey_id]));
  for (const w of model.walls ?? []) {
    out.push({
      storey: w.storey_id,
      item: { guid: w.guid, kind: 'wall', label: wallLabel(w), source: w.source, locked: w.locked, edited: isEdited(w.metadata, w.edited), uncertain: isUncertain(w.confidence, w.assumed) },
    });
  }
  for (const o of model.openings ?? []) {
    out.push({
      storey: o.storey_id ?? hostStorey.get(o.wall_guid) ?? null,
      item: { guid: o.guid, kind: 'opening', label: openingLabel(o), source: o.source, locked: o.locked, edited: isEdited(o.metadata, o.edited), uncertain: isUncertain(o.confidence) },
    });
  }
  for (const s of model.spaces ?? []) {
    out.push({
      storey: s.storey_id,
      item: { guid: s.guid, kind: 'space', label: spaceLabel(s), source: s.source, locked: s.locked, edited: isEdited(s.metadata, s.edited), uncertain: isUncertain(s.confidence) },
    });
  }
  for (const s of model.slabs ?? []) {
    out.push({
      storey: s.storey_id,
      item: { guid: s.guid, kind: 'slab', label: slabLabel(s), source: 'derived', locked: false, edited: false, uncertain: false },
    });
  }
  for (const s of model.stairs ?? []) {
    out.push({ storey: s.storey_id, item: { guid: s.guid, kind: 'stair', label: `Trappe ${s.id}`, source: s.source, locked: s.locked, edited: isEdited(s.metadata, s.edited), uncertain: false } });
  }
  return out;
}

export interface TreeOptions {
  /** "kun usikre": only uncertain rows; storeys and groups left empty are dropped. */
  uncertainOnly?: boolean;
  /** The Advarsler, so each row can count those that name it. */
  warnings?: readonly GeoWarning[];
}

/**
 * The element tree: every stored storey, top storey first (the order of a
 * section), then "Uden etage" when some element has none; per storey the
 * groups Vægge, Åbninger, Rum, Dæk & tag in that order, rows by id.
 */
export function elementTree(model: GeometriModel, opts: TreeOptions = {}): TreeStorey[] {
  const warnCount = new Map<string, number>();
  for (const w of opts.warnings ?? []) for (const g of w.guids) warnCount.set(g, (warnCount.get(g) ?? 0) + 1);
  const rows = itemsOf(model).filter((r) => !opts.uncertainOnly || r.item.uncertain);
  const storeys = [...(model.storeys ?? [])].sort((a, b) => b.id - a.id);
  const known = new Set(storeys.map((s) => s.id));
  const levels: { id: number | null; name: string }[] = storeys.map((s) => ({ id: s.id, name: s.name }));
  if (rows.some((r) => r.storey === null || !known.has(r.storey))) levels.push({ id: null, name: NO_STOREY });

  const out: TreeStorey[] = [];
  for (const level of levels) {
    const mine = rows.filter((r) => (level.id === null ? r.storey === null || !known.has(r.storey) : r.storey === level.id));
    const groups: TreeGroup[] = [];
    for (const kind of GROUP_ORDER) {
      const items = mine.filter((r) => r.item.kind === kind).map((r) => ({ ...r.item, warnings: warnCount.get(r.item.guid) ?? 0 }));
      if (items.length > 0) groups.push({ kind, label: GROUP_LABEL[kind], items });
    }
    if (opts.uncertainOnly && groups.length === 0) continue;
    out.push({ id: level.id, name: level.name, groups, count: mine.length });
  }
  return out;
}

// ------------------------------------------------------------ lookup --

export type FoundElement =
  | { kind: 'wall'; row: SolidWall }
  | { kind: 'opening'; row: SolidOpening }
  | { kind: 'space'; row: SolidSpace }
  | { kind: 'slab'; row: SolidSlab }
  | { kind: 'stair'; row: Stair };

export function findElement(model: GeometriModel, guid: string | null): FoundElement | null {
  if (guid === null) return null;
  const wall = model.walls?.find((r) => r.guid === guid);
  if (wall) return { kind: 'wall', row: wall };
  const opening = model.openings?.find((r) => r.guid === guid);
  if (opening) return { kind: 'opening', row: opening };
  const space = model.spaces?.find((r) => r.guid === guid);
  if (space) return { kind: 'space', row: space };
  const slab = model.slabs?.find((r) => r.guid === guid);
  if (slab) return { kind: 'slab', row: slab };
  const stair = model.stairs?.find((r) => r.guid === guid);
  return stair ? { kind: 'stair', row: stair } : null;
}

/** The storey an element is on (an opening falls back to its host's). */
export function elementStorey(model: GeometriModel, found: FoundElement): number | null {
  if (found.kind === 'opening' && found.row.storey_id === null) {
    return model.walls?.find((w) => w.guid === found.row.wall_guid)?.storey_id ?? null;
  }
  return found.row.storey_id;
}

// --------------------------------------------------------- inspector --

const SOURCE_LABEL: Record<GeoSource, string> = { detected: 'Detekteret', manual: 'Manuel', imported: 'Importeret', derived: 'Afledt' };
const OPENING_KIND: Record<SolidOpening['kind'], string> = { door: 'Dør', window: 'Vindue', opening: 'Åbning' };

export interface InspectorView {
  title: string;
  kindLabel: string;
  source: GeoSource;
  sourceLabel: string;
  locked: boolean;
  edited: boolean;
  /** The storey's name, or "Uden etage". */
  storey: string;
  /** Label, value — read-only in S1. */
  fields: [string, string][];
  /** A wall's openings, an opening's host: click to select. */
  related: { guid: string; label: string; role?: string }[];
}

const len = (w: Pick<SolidWall, 'start' | 'end'>) => Math.hypot(w.end[0] - w.start[0], w.end[1] - w.start[1]);
const pct = (c: number) => `${Math.round(c * 100)} %`;

export function inspectorView(model: GeometriModel, guid: string | null): InspectorView | null {
  const found = findElement(model, guid);
  if (!found) return null;
  const sid = elementStorey(model, found);
  const storey = (sid !== null ? model.storeys?.find((s) => s.id === sid)?.name : undefined) ?? NO_STOREY;
  const confidence = (c: number | null): [string, string][] => (c === null ? [] : [['Sikkerhed', pct(c)]]);
  if (found.kind === 'stair') {
    const t = found.row;
    const n = t.runs.reduce((a, r) => a + r.risers, 0);
    return {
      title: `Trappe ${t.id}`,
      kindLabel: 'Trappe',
      source: t.source,
      sourceLabel: SOURCE_LABEL[t.source],
      locked: t.locked,
      edited: isEdited(t.metadata, t.edited),
      storey,
      fields: [
        ['Trin', String(n)],
        ['Stigning', n > 0 ? formatLength((t.z_top - t.z_base) / n) : 'ukendt'],
        ['Bredde', formatLength(t.runs[0]?.width ?? 0)],
      ],
      related: [],
    };
  }
  switch (found.kind) {
    case 'wall': {
      const w = found.row;
      return {
        title: wallLabel(w),
        kindLabel: 'Væg',
        source: w.source,
        sourceLabel: SOURCE_LABEL[w.source],
        locked: w.locked,
        edited: isEdited(w.metadata, w.edited),
        storey,
        fields: [
          ['Længde', formatLength(len(w))],
          ['Tykkelse', w.thickness === null ? 'ukendt' : `${formatLength(w.thickness)}${w.assumed ? ' (antaget)' : ''}`],
          ['Højde', formatLength(w.height)],
          ['Underkant', formatLength(w.z_base)],
          ['Ydervæg', w.is_external ? 'Ja' : 'Nej'],
          ...confidence(w.confidence),
        ],
        related: (model.openings ?? []).filter((o) => o.wall_guid === w.guid).map((o) => ({ guid: o.guid, label: openingLabel(o) })),
      };
    }
    case 'opening': {
      const o = found.row;
      const host = model.walls?.find((w) => w.guid === o.wall_guid);
      return {
        title: openingLabel(o),
        kindLabel: OPENING_KIND[o.kind],
        source: o.source,
        sourceLabel: SOURCE_LABEL[o.source],
        locked: o.locked,
        edited: isEdited(o.metadata, o.edited),
        storey,
        fields: [
          ['Bredde', formatLength(Math.abs(o.s_end - o.s_start))],
          ['Højde', formatLength(o.z_head - o.z_sill)],
          ['Brystning', formatLength(o.z_sill)],
          ...confidence(o.confidence),
        ],
        related: host ? [{ guid: host.guid, label: wallLabel(host), role: 'Vært' }] : [],
      };
    }
    case 'space': {
      const s = found.row;
      return {
        title: spaceLabel(s),
        kindLabel: 'Rum',
        source: s.source,
        sourceLabel: SOURCE_LABEL[s.source],
        locked: s.locked,
        edited: isEdited(s.metadata, s.edited),
        storey,
        fields: [
          ...(s.area !== null ? ([['Areal', formatAreaDa(s.area)]] as [string, string][]) : []),
          ['Loftshøjde', formatLength(s.z_ceiling - s.z_floor)],
          ['Gulv', formatLength(s.z_floor)],
          ...confidence(s.confidence),
        ],
        related: [],
      };
    }
    case 'slab': {
      const s = found.row;
      return {
        title: slabLabel(s),
        kindLabel: s.kind === 'roof' ? 'Tag' : 'Dæk',
        source: 'derived',
        sourceLabel: SOURCE_LABEL.derived,
        locked: false,
        edited: false,
        storey,
        fields: [
          ['Areal', formatAreaDa(s.area)],
          ['Tykkelse', formatLength(s.z_top - s.z_base)],
        ],
        related: [],
      };
    }
  }
}

// ---------------------------------------------------------- warnings --

export type WarningKind = 'opening_outside' | 'short_wall' | 'loose_end' | 'double_wall' | 'space_self_intersection';

export interface GeoWarning {
  id: string;
  kind: WarningKind;
  /** The elements it is about, the one at fault first (select and zoom to them). */
  guids: string[];
  text: string;
}

/** An opening may overhang its host's end by this much (rounding). */
const OPENING_SLACK = 0.01;
/** An end within its neighbour's half thickness plus this is joined to it. */
const JOIN_SLACK = 0.01;
/** An unjoined end this close to a wall's face is a likely gap. */
const GAP_RANGE = 0.1;
/** Walls this close to parallel (radians, ~5°) may be doubles. */
const PARALLEL = (5 * Math.PI) / 180;
/** Doubles must overlap along their axes by at least this much. */
const MIN_OVERLAP = 0.2;

interface Seg {
  w: SolidWall;
  a: XY;
  b: XY;
  d: XY;
  length: number;
  half: number;
}

function seg(w: SolidWall): Seg | null {
  const length = len(w);
  if (!(length > 1e-6)) return null;
  const d: XY = [(w.end[0] - w.start[0]) / length, (w.end[1] - w.start[1]) / length];
  return { w, a: w.start, b: w.end, d, length, half: (w.thickness ?? 0) / 2 };
}

/** Along-axis position of `p` on `s` (metres from its start) and its distance off the axis line. */
function project(s: Seg, p: XY): { t: number; off: number } {
  const vx = p[0] - s.a[0];
  const vy = p[1] - s.a[1];
  return { t: vx * s.d[0] + vy * s.d[1], off: Math.abs(vx * s.d[1] - vy * s.d[0]) };
}

/** Distance from `p` to the segment `s`. */
function distToSeg(s: Seg, p: XY): number {
  const { t, off } = project(s, p);
  if (t < 0) return Math.hypot(p[0] - s.a[0], p[1] - s.a[1]);
  if (t > s.length) return Math.hypot(p[0] - s.b[0], p[1] - s.b[1]);
  return off;
}

function segmentsCross(p1: XY, p2: XY, p3: XY, p4: XY): boolean {
  const o = (a: XY, b: XY, c: XY) => Math.sign((b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]));
  const d1 = o(p3, p4, p1);
  const d2 = o(p3, p4, p2);
  const d3 = o(p1, p2, p3);
  const d4 = o(p1, p2, p4);
  return d1 * d2 < 0 && d3 * d4 < 0;
}

/** Whether a ring's non-adjacent edges cross. */
export function ringSelfIntersects(ring: readonly XY[]): boolean {
  const n = ring.length;
  if (n < 4) return false;
  for (let i = 0; i < n; i += 1) {
    for (let j = i + 1; j < n; j += 1) {
      if (j === i + 1 || (i === 0 && j === n - 1)) continue;
      if (segmentsCross(ring[i], ring[(i + 1) % n], ring[j], ring[(j + 1) % n])) return true;
    }
  }
  return false;
}

const cm = (m: number) => `${Math.round(m * 100)} cm`;

/**
 * The client-side checks (Advarsler): an opening outside its host or with
 * no host; a wall shorter than its thickness; a wall end joined to nothing
 * that stops within 10 cm of another wall's face (a likely gap); two
 * near-parallel walls closer than their thicknesses over 20 cm or more (a
 * likely double wall); a space whose outline crosses itself. Walls are only
 * compared within a storey.
 */
export function geometryWarnings(model: GeometriModel): GeoWarning[] {
  const out: GeoWarning[] = [];
  const walls = model.walls ?? [];
  const byGuid = new Map(walls.map((w) => [w.guid, w]));

  for (const o of model.openings ?? []) {
    const host = byGuid.get(o.wall_guid);
    const label = openingLabel(o);
    if (!host) {
      out.push({ id: `opening_outside:${o.guid}`, kind: 'opening_outside', guids: [o.guid], text: `${label} har ingen værtsvæg` });
      continue;
    }
    const lo = Math.min(o.s_start, o.s_end);
    const hi = Math.max(o.s_start, o.s_end);
    if (lo < -OPENING_SLACK || hi > len(host) + OPENING_SLACK) {
      out.push({ id: `opening_outside:${o.guid}`, kind: 'opening_outside', guids: [o.guid, host.guid], text: `${label} går ud over ${wallLabel(host)}` });
    }
  }

  for (const w of walls) {
    if (w.thickness !== null && len(w) < w.thickness) {
      out.push({ id: `short_wall:${w.guid}`, kind: 'short_wall', guids: [w.guid], text: `${wallLabel(w)} er kortere end sin tykkelse` });
    }
  }

  const segs = walls.map(seg).filter((s): s is Seg => s !== null);
  for (const s of segs) {
    const others = segs.filter((o) => o !== s && o.w.storey_id === s.w.storey_id);
    for (const [end, p] of [
      ['start', s.a],
      ['end', s.b],
    ] as const) {
      if (others.some((o) => distToSeg(o, p) <= o.half + JOIN_SLACK)) continue;
      // The nearest wall whose body the end faces (its projection falls on the wall).
      let best: { o: Seg; gap: number } | null = null;
      for (const o of others) {
        const { t, off } = project(o, p);
        if (t < 0 || t > o.length) continue;
        const gap = off - o.half;
        if (gap > JOIN_SLACK && gap <= GAP_RANGE + JOIN_SLACK && (!best || gap < best.gap)) best = { o, gap };
      }
      if (best) {
        out.push({
          id: `loose_end:${s.w.guid}:${end}`,
          kind: 'loose_end',
          guids: [s.w.guid],
          text: `${wallLabel(s.w)}: løs ende ${cm(best.gap)} fra ${wallLabel(best.o.w)}`,
        });
      }
    }
  }

  for (let i = 0; i < segs.length; i += 1) {
    for (let j = i + 1; j < segs.length; j += 1) {
      const a = segs[i];
      const b = segs[j];
      if (a.w.storey_id !== b.w.storey_id) continue;
      const cos = Math.abs(a.d[0] * b.d[0] + a.d[1] * b.d[1]);
      if (cos < Math.cos(PARALLEL)) continue;
      const mid: XY = [(b.a[0] + b.b[0]) / 2, (b.a[1] + b.b[1]) / 2];
      if (project(a, mid).off >= Math.max(a.half + b.half, JOIN_SLACK * 2)) continue;
      const t1 = project(a, b.a).t;
      const t2 = project(a, b.b).t;
      const overlap = Math.min(a.length, Math.max(t1, t2)) - Math.max(0, Math.min(t1, t2));
      if (overlap < MIN_OVERLAP) continue;
      out.push({
        id: `double_wall:${a.w.guid}:${b.w.guid}`,
        kind: 'double_wall',
        guids: [a.w.guid, b.w.guid],
        text: `${wallLabel(a.w)} og ${wallLabel(b.w)} overlapper — mulig dobbeltvæg`,
      });
    }
  }

  for (const s of model.spaces ?? []) {
    if (ringSelfIntersects(s.polygon.outer)) {
      out.push({ id: `space_self_intersection:${s.guid}`, kind: 'space_self_intersection', guids: [s.guid], text: `${spaceLabel(s)} krydser sig selv` });
    }
  }
  return out;
}

// ------------------------------------------------------------ picking --

/**
 * The wall or opening at plan point `p` (world x/y): an opening on its host
 * first (it is drawn over the wall), else the nearest wall whose axis is
 * within its half thickness or `tol` metres. Null for neither.
 */
export function pickPlanElement(walls: readonly SolidWall[], openings: readonly SolidOpening[], p: XY, tol: number): string | null {
  const segs = new Map<string, Seg>();
  for (const w of walls) {
    const s = seg(w);
    if (s) segs.set(w.guid, s);
  }
  for (const o of openings) {
    const s = segs.get(o.wall_guid);
    if (!s) continue;
    const { t, off } = project(s, p);
    if (t >= Math.min(o.s_start, o.s_end) && t <= Math.max(o.s_start, o.s_end) && off <= Math.max(s.half, tol)) return o.guid;
  }
  let best: { guid: string; d: number } | null = null;
  for (const s of segs.values()) {
    const d = distToSeg(s, p);
    if (d <= Math.max(s.half, tol) && (!best || d < best.d)) best = { guid: s.w.guid, d };
  }
  return best?.guid ?? null;
}

// --------------------------------------------------------------- zoom --

export interface WorldBox {
  min: { x: number; y: number; z: number };
  max: { x: number; y: number; z: number };
}

/** Room around an element when the view zooms to it, metres. */
const ZOOM_PAD = 0.5;

function boxOf(points: readonly XY[], zMin: number, zMax: number, half = 0): WorldBox | null {
  if (points.length === 0) return null;
  const xs = points.map((p) => p[0]);
  const ys = points.map((p) => p[1]);
  const pad = ZOOM_PAD + half;
  const r = (v: number) => Math.round(v * 1e6) / 1e6;
  return {
    min: { x: r(Math.min(...xs) - pad), y: r(Math.min(...ys) - pad), z: r(zMin - ZOOM_PAD) },
    max: { x: r(Math.max(...xs) + pad), y: r(Math.max(...ys) + pad), z: r(zMax + ZOOM_PAD) },
  };
}

/**
 * The world box to zoom to for an element (padded); an opening is boxed
 * on its host's axis. Null when it is unknown or has no host.
 */
export function elementBox(model: GeometriModel, guid: string | null): WorldBox | null {
  const found = findElement(model, guid);
  if (!found) return null;
  if (found.kind === 'stair') {
    const t = found.row;
    const pts = [...t.runs.flatMap((r) => [r.start, r.end]), ...t.landings.flatMap((l) => l.outer)] as XY[];
    return boxOf(pts, t.z_base, t.z_top, Math.max(0, ...t.runs.map((r) => r.width)));
  }
  switch (found.kind) {
    case 'wall': {
      const w = found.row;
      return boxOf([w.start, w.end], w.z_base, w.z_top, (w.thickness ?? 0) / 2);
    }
    case 'opening': {
      const o = found.row;
      const host = model.walls?.find((w) => w.guid === o.wall_guid);
      const s = host ? seg(host) : null;
      if (!s) return null;
      const at = (t: number): XY => [s.a[0] + s.d[0] * t, s.a[1] + s.d[1] * t];
      return boxOf([at(o.s_start), at(o.s_end)], o.z_sill, o.z_head, s.half);
    }
    case 'space':
      return boxOf(found.row.polygon.outer, found.row.z_floor, found.row.z_ceiling);
    case 'slab':
      return boxOf(found.row.polygon.outer, found.row.z_base, found.row.z_top);
  }
}
